import clsx from 'clsx';
import { Check, FileSpreadsheet, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router';
import { isWeekend, weekStart } from '@pz/core';
import { BulkHoursDialog } from '../components/approvals-bulk';
import { BilledCell, BilledDialog, EntryDialog, TaskBadges } from '../components/entry';
import { ScrollBox, Sentinel, useChunked } from '../components/virtual';
import { Button, Checkbox, ConfirmModal, EmptyState, ErrorBox, Loading, PageHeader, Pill, Select, StatusPill, useToast } from '../components/ui';
import { d, h, hh, kw, wd } from '../lib/format';
import { useM, useQ } from '../lib/hooks';
import { useCategories } from '../lib/categories';
import { useMe } from '../lib/me';
import { rowCheck, useRangeSelection } from '../lib/selection';
import type { EntryView } from '@pz/server';

/**
 * Freigaben: time entries of other people (project leads first stage, office or management final stage).
 * Absence requests are decided on their own page ("Abwesenheitsanträge"); an old link with ?tab=leave goes there.
 */
export function ApprovalsPage() {
  const [params] = useSearchParams();
  if (params.get('tab') === 'leave') return <Navigate to="/leave-requests" replace />;
  return (
    <div className="pb-16">
      <TimeApprovals />
    </div>
  );
}

/** entries per page of the queue and per request of a bulk decision */
const PAGE = 2000;
const PAGE_MAX = 5000;
const BATCH = 1000;

function TimeApprovals() {
  const { t } = useTranslation();
  const { can } = useMe();
  const toast = useToast();
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const [projectId, setProjectId] = useState('');
  const [reject, setReject] = useState(false);
  const [edit, setEdit] = useState<EntryView | null>(null);
  const [billed, setBilled] = useState<EntryView | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  // filters of the queue: person and kind of task (Forma, manual, category); long queues are read in pages
  const [personId, setPersonId] = useState('');
  const [task, setTask] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const filterKey = `${scope}|${projectId}|${personId}|${task}`;
  const q = useQ('approvals.queue', { scope, projectId: projectId || undefined, personId: personId || undefined, task: (task || undefined) as 'forma' | undefined, limit });
  const cats = useCategories();
  const projects = useQ('projects.list', {});
  const inv = { invalidate: ['approvals.', 'time.', 'kpi.', 'billing.'] };
  const approve = useM('approvals.approve', inv);
  const rejectM = useM('approvals.reject', inv);
  const undo = useM('approvals.undo', inv);
  const items = q.data?.items ?? [];

  const groups = useMemo(() => {
    const map = new Map<string, { key: string; person: string; week: string; items: typeof items }>();
    for (const i of items) {
      const ws = weekStart(i.workDate);
      const key = `${i.personId}:${ws}`;
      const g = map.get(key) ?? { key, person: i.personName, week: ws, items: [] };
      g.items.push(i);
      map.set(key, g);
    }
    return [...map.values()].sort((a, b) => a.person.localeCompare(b.person) || a.week.localeCompare(b.week));
  }, [items]);
  const chunk = useChunked(groups, 20, filterKey);
  // visible order for Shift ranges across all groups
  const order = useMemo(() => groups.flatMap((g) => g.items.map((i) => i.id)), [groups]);
  const sel = useRangeSelection(order);
  const selected = sel.selected;

  const runUndo = (ids: string[]) =>
    undo
      .mutateAsync({ ids })
      .then((u) => {
        toast.ok(t('approvals.undone', { count: u.undone }));
        if (u.skipped.length) toast.warn(t('approvals.undoSkipped', { count: u.skipped.length }));
      })
      .catch(toast.error);

  const doApprove = async (ids: string[]) => {
    try {
      // large selections in parts of 1000 (one request each)
      let approved = 0;
      const skipped: { id: string }[] = [];
      for (let i = 0; i < ids.length; i += BATCH) {
        const r = await approve.mutateAsync({ ids: ids.slice(i, i + BATCH) });
        approved += r.approved;
        skipped.push(...r.skipped);
      }
      const done = ids.filter((id) => !skipped.some((x) => x.id === id));
      toast.undoable(t('approvals.done', { count: approved }), t('common.undo'), () => runUndo(done.slice(0, 2000)));
      if (skipped.length) toast.warn(t('approvals.skipped', { count: skipped.length }));
      sel.clear();
    } catch (e) {
      toast.error(e);
    }
  };

  return (
    <div>
      <PageHeader
        title={t('approvals.title')}
        subtitle={t('approvals.subtitle')}
        actions={
          <>
            <Button icon={<FileSpreadsheet className="size-4" />} onClick={() => setBulkOpen(true)} title={t('approvals.bulk.title')}>
              {t('approvals.bulk.open')}
            </Button>
            {can('time.manage') && (
              <Select className="w-44" value={scope} onChange={(e) => setScope(e.target.value as 'mine' | 'all')} aria-label={t('approvals.mine')}>
                <option value="mine">{t('approvals.mine')}</option>
                <option value="all">{t('approvals.all')}</option>
              </Select>
            )}
            <Select className="w-64" value={projectId} onChange={(e) => (setProjectId(e.target.value), setLimit(PAGE))} aria-label={t('common.project')}>
              <option value="">{t('common.all')}</option>
              {(projects.data?.projects ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.number} {p.name}
                </option>
              ))}
            </Select>
            <Select className="w-52" value={personId} onChange={(e) => (setPersonId(e.target.value), setLimit(PAGE))} aria-label={t('approvals.filterPerson')}>
              <option value="">{t('approvals.allPersons')}</option>
              {(q.data?.facets.persons ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.count})
                </option>
              ))}
              {personId && !(q.data?.facets.persons ?? []).some((p) => p.id === personId) && <option value={personId}>…</option>}
            </Select>
            <Select className="w-52" value={task} onChange={(e) => (setTask(e.target.value), setLimit(PAGE))} aria-label={t('approvals.filterKind')}>
              <option value="">{t('approvals.allKinds')}</option>
              {(q.data?.facets.kinds ?? []).map((k) => (
                <option key={k.key} value={k.key}>
                  {k.key === 'forma' ? t('approvals.kindForma') : k.key === 'manual' ? t('approvals.kindManual') : cats.label(k.key.slice(4))} ({k.count})
                </option>
              ))}
              {task && !(q.data?.facets.kinds ?? []).some((k) => k.key === task) && <option value={task}>…</option>}
            </Select>
          </>
        }
      />
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && items.length === 0 && <EmptyState>{t('approvals.nothing')}</EmptyState>}
      {items.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 text-sm">
          <Checkbox label={t('common.selectAll')} checked={sel.all} onChange={sel.toggleAll} />
          <span className="text-xs text-graphite">{t('common.shiftHint')}</span>
          {q.data && (
            <span className="ml-auto text-xs text-graphite">
              {t('approvals.summaryLine', { shown: items.length, total: q.data.total, hours: h(q.data.totalMinutes) })}
            </span>
          )}
        </div>
      )}
      {q.data?.hasMore && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-line bg-sheet-2 px-3 py-2 text-sm">
          <span>{t('approvals.moreHint', { shown: items.length, total: q.data.total })}</span>
          {limit < PAGE_MAX && (
            <Button size="sm" loading={q.isFetching} onClick={() => setLimit(Math.min(PAGE_MAX, limit + PAGE))}>
              {t('approvals.loadMore')}
            </Button>
          )}
        </div>
      )}
      <ScrollBox maxHeight="min(74vh, 60rem)" label={t('approvals.title')} className="flex flex-col gap-4 pr-1">
        {chunk.visible.map((g) => {
          const ids = g.items.map((i) => i.id);
          const all = ids.every((id) => selected.has(id));
          const total = g.items.reduce((s, i) => s + i.minutes, 0);
          return (
            <section key={g.key} className="rounded-lg border border-line bg-sheet">
              <header className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-2">
                <Checkbox label={<span className="font-semibold">{g.person}</span>} checked={all} onChange={(e) => sel.setMany(ids, e.target.checked)} />
                <span className="num text-sm text-graphite">
                  {t('approvals.weekOf', { week: kw(g.week) })} · {d(g.week)}
                </span>
                <span className="num ml-auto text-sm font-semibold">{hh(total)}</span>
                <Button size="sm" icon={<Check className="size-3.5" />} onClick={() => doApprove(ids)} loading={approve.isPending}>
                  {t('approvals.approve')}
                </Button>
              </header>
              <ul>
                {g.items.map((i) => (
                  <li key={i.id} className={clsx('flex flex-wrap items-start gap-3 border-b border-line/60 px-4 py-2 text-sm last:border-0', selected.has(i.id) && 'bg-accent-soft/50')}>
                    <Checkbox label={<span className="sr-only">{t('common.select')}</span>} {...rowCheck(sel, i.id)} />
                    <span className={clsx('num w-24 shrink-0', isWeekend(i.workDate) ? 'font-medium text-warn' : 'text-graphite')} title={isWeekend(i.workDate) ? t('approvals.weekendHint') : undefined}>
                      {wd(i.workDate)} {d(i.workDate).slice(0, 6)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="num font-semibold text-accent">{i.taskRef}</span>
                        <span>{i.taskTitle}</span>
                        <TaskBadges formaState={i.taskFormaState} internal={i.projectInternal} deleted={i.taskDeleted} />
                      </div>
                      <div className="text-xs text-graphite">
                        {i.projectNumber} {i.projectName} · {t(`billingType.${i.billingType}`)}
                        {i.workLocation ? ` · ${i.workLocation}` : ''}
                      </div>
                      {i.description && <div className="mt-0.5 whitespace-pre-line text-graphite">{i.description}</div>}
                    </div>
                    <span className="flex flex-col items-end gap-1">
                      <span className="num font-semibold">{h(i.minutes)}</span>
                      <BilledCell entry={i} onEdit={setBilled} />
                      <span className="flex gap-1">
                        <StatusPill status={i.status} />
                        <Pill tone={i.stage === 'override' ? 'warn' : 'neutral'}>{t(`approvals.stage.${i.stage}`)}</Pill>
                      </span>
                      {i.perm.edit && (
                        <button className="text-xs text-accent hover:underline" onClick={() => setEdit(i)}>
                          {t('common.edit')}
                        </button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
        {chunk.hasMore && <Sentinel onMore={chunk.more} />}
      </ScrollBox>
      {chunk.hasMore && <p className="mt-2 text-xs text-graphite">{t('approvals.groupsShown', { shown: chunk.shown, total: chunk.total })}</p>}

      {selected.size > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-sheet px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}>
          <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-2">
            <span className="text-sm">{t('common.selected', { count: selected.size })}</span>
            <span className="num text-sm text-graphite">
              {hh(items.filter((i) => selected.has(i.id)).reduce((s, i) => s + i.minutes, 0))}
            </span>
            <div className="ml-auto flex gap-2">
              <Button variant="danger" icon={<X className="size-4" />} onClick={() => setReject(true)}>
                {t('approvals.reject')}
              </Button>
              <Button variant="primary" icon={<Check className="size-4" />} loading={approve.isPending} onClick={() => doApprove([...selected])}>
                {t('approvals.approve')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={reject}
        onClose={() => setReject(false)}
        title={t('approvals.rejectTitle')}
        confirmLabel={t('approvals.reject')}
        danger
        reason="required"
        reasonLabel={t('approvals.rejectReason')}
        loading={rejectM.isPending}
        error={rejectM.error}
        onConfirm={async (reason) => {
          try {
            const ids = [...selected];
            let rejected = 0;
            const skipped: { id: string }[] = [];
            for (let i = 0; i < ids.length; i += BATCH) {
              const r = await rejectM.mutateAsync({ ids: ids.slice(i, i + BATCH), reason });
              rejected += r.rejected;
              skipped.push(...r.skipped);
            }
            const done = ids.filter((id) => !skipped.some((x) => x.id === id));
            toast.undoable(t('approvals.rejected', { count: rejected }), t('common.undo'), () => runUndo(done.slice(0, 2000)));
            sel.clear();
            setReject(false);
          } catch {
            // shown in the dialog
          }
        }}
      />
      <EntryDialog open={!!edit} entry={edit} onClose={() => setEdit(null)} />
      <BilledDialog entry={billed} onClose={() => setBilled(null)} />
      <BulkHoursDialog open={bulkOpen} onClose={() => setBulkOpen(false)} />
    </div>
  );
}
